import isNil from 'lodash/isNil';

import type { GetReportsListResponse } from '@appTypes/webscript';
import { REPORTS_ALT_BASE_URL } from '@config/config';
import {
  ADV_HISTORY_CTX_API,
  CustomReports,
  reportCodes,
} from '@modules/reports/constants';

import { individualReports, reportsV2 } from '../constants';

export function getCustomReportCode(report: CustomReports): string {
  return reportCodes[report];
}

export function getCustomReportUrl(reportCode: string) {
  const fallbackReportUrl = new URL(
    '/advancements/webscript/output/sbreports.aspx?method=executescriptset&scriptcode=ReportPresentation&ApplicationType=SBL',
    REPORTS_ALT_BASE_URL,
  );
  fallbackReportUrl.searchParams.set('scriptsetcode', reportCode);

  return fallbackReportUrl.toString();
}

/**
 * Given a set of selected users on the roster, this API returns a token we can use for generating
 * reports considering only those selected users.
 */
export async function getContextToken(
  organizationGuid: string,
  personGuids: string[],
  reportCode: string,
) {
  if (!organizationGuid || !personGuids) return { ContextToken: '' };

  const scoutList = personGuids.filter(Boolean).join(',');

  try {
    const targetUrl = `${REPORTS_ALT_BASE_URL}${ADV_HISTORY_CTX_API}&OrganizationGUID=${organizationGuid}&ScriptSetCode=${reportCode}`;

    const response = await fetch(targetUrl, {
      method: 'POST',
      body: `ScoutList=${scoutList}`,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'include',
    });
    const data = (await response.json()) as { ContextToken: string };
    return data;
  } catch {
    // nothing
  }

  return { ContextToken: '' };
}

export type NormalizedReportItem = {
  /** Normalized property for either ScriptSetcode or ReportCode (for council) */
  reportCode: string;
  isIndividual: boolean;
  ReportURI: string;
  ReportName: string;
  ReportGroupName: string;
  /** Use `reportCode` instead! */
  ReportCode: string;
  id: number;
};

export function processResponse(
  response: GetReportsListResponse,
): NormalizedReportItem[] {
  const reportsResponse = response
    ? response.Reports.filter(report => !isNil(report))
        .map((report, i) => {
          const { ReportCode: reportCode = '' } = report;
          const hasV2 = (reportsV2 as string[]).includes(reportCode);
          const isIndividual = (individualReports as string[]).includes(
            reportCode,
          );
          const url = new URL(report.ReportURI);

          const { searchParams } = url;
          // some urls have already application type, so we use URL interface to avoid duplicates
          searchParams.set('ApplicationType', 'SBL');
          hasV2 && searchParams.set('activityVersion', '2');
          url.search = searchParams.toString();
          return {
            id: i + 1,
            ...report,
            reportCode,
            isIndividual,
            ReportURI: url.toString(),
          };
        })
        .filter(
          // Dens are not supported yet, so we can omit them
          ({ ReportName }) => !ReportName.toLowerCase().includes('by den'),
        )
    : [];
  return reportsResponse;
}
